import { teamsCalendarsText } from './getSelectedTeamsCalendarsService.locstring.json';
import loc from 'owa-localize';
import { Calendar, CalendarGroupExtended, joinFolderIdAndChannelId } from 'owa-calendar-cache';
import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import CalendarGroupType from 'owa-service/lib/contract/CalendarGroupType';
import LinkedCalendarEntry from 'owa-service/lib/contract/LinkedCalendarEntry';
import { TEAMS_CALENDARS_GROUP_ID } from '../constants';
import { MailboxInfo, getUserMailboxInfo } from 'owa-client-ids';
import { TEAMSCALENDAR_ENTRY_TYPE } from 'owa-calendar-properties';
import { TeamsChannel } from 'owa-teams-sds';
import {
    TeamsCalendarsResult,
    GetSelectedTeamsChannelsCalendarsServiceResult,
} from './getSelectedTeamsCalendarsService';
import { extractHostFromChannelId } from 'owa-calendar-teams-utils';

export function processResponse(
    channelsSet: TeamsChannel[]
): GetSelectedTeamsChannelsCalendarsServiceResult | null {
    const groupCalendarsResult: TeamsCalendarsResult[] = [];
    const resultCalendars: Calendar[] = [];

    channelsSet.forEach((selectedChannel: TeamsChannel) => {
        const groupMailboxInfo: MailboxInfo = createMailBoxInfoFromTeamsChannel(selectedChannel);

        const calendarEntry: LinkedCalendarEntry = createLinkedCalendarEntryFromTeamsChannel(
            selectedChannel
        );

        const calendarFolder: CalendarFolder = createCalendarFolderFromTeamsChannel(
            selectedChannel
        );

        const calendar: Calendar = createCalendarFromTeamsChannel(selectedChannel);

        groupCalendarsResult.push({
            entry: calendarEntry,
            folder: calendarFolder,
            mailboxInfo: groupMailboxInfo,
        });

        resultCalendars.push(calendar);
    });

    if (resultCalendars.length > 0) {
        const userMailboxInfo = getUserMailboxInfo();
        const groupsCalendarGroupMailboxInfo: MailboxInfo = {
            type: 'TeamsMailbox',
            userIdentity: userMailboxInfo.userIdentity, // identity of the account to which these teams calendars are associated with
            mailboxSmtpAddress: userMailboxInfo.mailboxSmtpAddress, // mailboxSmtpAddress of the account to which these teams calendars are associated with
        };
        const resultGroup: CalendarGroupExtended = {
            id: { id: TEAMS_CALENDARS_GROUP_ID, mailboxInfo: groupsCalendarGroupMailboxInfo },
            GroupId: TEAMS_CALENDARS_GROUP_ID,
            ItemId: { Id: TEAMS_CALENDARS_GROUP_ID },
            GroupName: loc(teamsCalendarsText),
            GroupType: CalendarGroupType.Normal,
        };

        return {
            groupCalendars: groupCalendarsResult,
            calendarGroup: resultGroup,
        };
    } else {
        return null;
    }
}

export function createCalendarFromTeamsChannel(selectedChannel: TeamsChannel): Calendar | null {
    // In order to save a unique Calendar id for a TEAMS channel, we are saving the channel id  as the Calendar Id.
    // Since these are logged in our code base, we can't have an emailAddress as Calendar id, hence we only save the host part.
    const calendar: Calendar = {
        id: {
            id: extractHostFromChannelId(selectedChannel.channelId),
            mailboxInfo: createMailBoxInfoFromTeamsChannel(selectedChannel),
        },
        calculatedFolderId: selectedChannel.teamSmtpAddress.toLowerCase(),
        displayName: selectedChannel.channelName + ' - ' + selectedChannel.teamName,
        colorScheme: null,
    };

    if (calendar) {
        return calendar;
    } else {
        return null;
    }
}

export function createMailBoxInfoFromTeamsChannel(
    selectedChannel: TeamsChannel
): MailboxInfo | null {
    const teamSmtpEmailAddress: string = selectedChannel.teamSmtpAddress.toLowerCase();

    const teamsMailboxInfo: MailboxInfo = {
        type: 'TeamsMailbox',
        mailboxSmtpAddress: teamSmtpEmailAddress,
        userIdentity: getUserMailboxInfo().userIdentity, // identity of the the default account
    };

    if (teamsMailboxInfo) {
        return teamsMailboxInfo;
    } else {
        return null;
    }
}

export function createLinkedCalendarEntryFromTeamsChannel(
    selectedChannel: TeamsChannel
): LinkedCalendarEntry | null {
    const channelId = extractHostFromChannelId(selectedChannel.channelId);
    const calendarEntry: LinkedCalendarEntry = {
        __type: TEAMSCALENDAR_ENTRY_TYPE,
        CalendarColor: CalendarColor.Auto,
        CalendarName: selectedChannel.channelName + ' - ' + selectedChannel.teamName,
        OwnerEmailAddress: joinFolderIdAndChannelId(
            selectedChannel.teamSmtpAddress.toLowerCase(),
            channelId
        ),
        OwnerName: selectedChannel.channelName,
        IsGroupMailboxCalendar: true,
        IsGeneralScheduleCalendar: true,
        ItemId: { Id: channelId },
        ParentGroupId: TEAMS_CALENDARS_GROUP_ID,
    };

    if (calendarEntry) {
        return calendarEntry;
    } else {
        return null;
    }
}

export function createCalendarFolderFromTeamsChannel(
    selectedChannel: TeamsChannel
): CalendarFolder | null {
    // In order to save unique CalendarFolder entries for channels that belong to the same team,
    // we are appending the unique channel id to the TEAM's email address.
    const calendarFolder: CalendarFolder = {
        DistinguishedFolderId: joinFolderIdAndChannelId(
            selectedChannel.teamSmtpAddress.toLowerCase(),
            extractHostFromChannelId(selectedChannel.channelId)
        ),
        DisplayName: selectedChannel.channelName,
    };

    if (calendarFolder) {
        return calendarFolder;
    } else {
        return null;
    }
}
